import React, { useEffect, useState, useMemo } from 'react';
import { reduxForm, getFormSyncErrors, getFormValues, isDirty, initialize, destroy } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';

import { Blade, Button, ProgressIndicator } from '@flashtalking/rhombus-react';

import useActiveCreative from 'hooks/useActiveCreative';
import { MODAL_TYPES } from 'utils/constants';
import { closeModal } from 'reducers/Modal';
import { loadConcept, addNewAdstudioCreativesToConcept } from 'reducers/ConceptDetails';
import { ADD_ADSTUDIO_TO_CONCEPT_FORM } from 'widgets/forms/list';
import SelectAdsizesForConcept from './SelectAdsizesForConcept';
import { adSizes } from './CreateDisplayConceptModal';
import { customValidate } from './SelectCreativeTypeNew';

import './AddAdStudioCreativeToConcept.css';

export function buildInitialConceptSizes(conceptDetails, adSizes) {
	let IV = {
		"base-sizes":{},
		"custom-sizes":[],
	};

	conceptDetails?.forEach((v) => {
		let size = `${v.width}x${v.height}`;

		if(adSizes.indexOf(size) > -1){
			IV["base-sizes"][size] = true;
		} else {
			IV["custom-sizes"].push({
				width:`${v.width}`,
				height:`${v.height}`,
			});
		}
	});

	return IV;
}

function AddAdStudioCreativeToConcept(props){
	const { handleSubmit, pristine } = props;
	const {
		isOpen,
		modalType,
		modalOptions,
		conceptDetails,
		formErrors = { errors:[]},
		formValues,
		isEdited,
	} = useSelector(state => ({
		isOpen: state.modal.isOpen,
		modalType: state.modal.modalType,
		modalOptions: state.modal.modalOptions,
		conceptDetails: state.conceptDetails?.data?.sizes,
		formErrors: {
			errors: getFormSyncErrors(ADD_ADSTUDIO_TO_CONCEPT_FORM)(state),
		},
		formValues: getFormValues(ADD_ADSTUDIO_TO_CONCEPT_FORM)(state),
		isEdited: isDirty(ADD_ADSTUDIO_TO_CONCEPT_FORM)(state),
	}));
	const [isLoading, setIsLoading] = useState(false);

	const { libraryId, conceptId, creativeId } = modalOptions;

	const dispatch = useDispatch();

	useActiveCreative(creativeId);

	const isModalOpen = useMemo(() => {
		return modalType === MODAL_TYPES.ADD_ADSTUDIO_TO_CONCEPT && isOpen;
	}, [modalType, isOpen]);

	useEffect(() => {
		dispatch(destroy(ADD_ADSTUDIO_TO_CONCEPT_FORM));
		return () => {
			dispatch(destroy(ADD_ADSTUDIO_TO_CONCEPT_FORM));
		};
	}, []);

	useEffect(() => {
		setIsLoading(true);
		if(!libraryId || !conceptId || !isModalOpen) { return; }
		dispatch(loadConcept(libraryId, conceptId)).then((resp) => {
			setIsLoading(false);
		});
	}, [libraryId, conceptId, isModalOpen]);

	let initialValues = useMemo(() => {
		return buildInitialConceptSizes(conceptDetails, adSizes);
	}, [conceptDetails]);

	useEffect(() => {
		if(!initialValues || !isModalOpen) { return; }
		dispatch(initialize(ADD_ADSTUDIO_TO_CONCEPT_FORM, initialValues));
	}, [initialValues, isModalOpen]);

	if(!isModalOpen){ return null; }

	const renderContent = () => {
		return (
			<>
				<ProgressIndicator isLoading={isLoading} />
				<SelectAdsizesForConcept
					formName={ADD_ADSTUDIO_TO_CONCEPT_FORM}
					handleSubmit={handleSubmit}
					pristine={pristine}
					libraryId={libraryId}
					conceptId={conceptId}
					initialValues={initialValues}
					formErrors={formErrors}
					setIsLoading={setIsLoading}
				 />
			</>
		);
	};

	const onClose = () => {
		dispatch(closeModal());
	};

	 function customSizeValidation(value){
		const { width, height } = value;
		return width === undefined || height === undefined;
	}

	const customSizeValResult = formValues?.['custom-sizes']?.some(customSizeValidation);

	const buttonValidation = () => {
		const customErrors = formErrors.errors?.customErrors;
		if(formValues){Object.entries(formValues).some(v => v[1]);}

		if(formErrors.errors?.['custom-sizes'] || isLoading || customErrors || customSizeValResult){return true;}
	};

	const renderFooter = () => {
		return (
			<>
				<Button key="cancel" theme="grey" onClick={() => onClose()}>Cancel</Button>
				<Button key="submit" theme="blue" disabled={buttonValidation() || !isEdited} type="submit" form={ADD_ADSTUDIO_TO_CONCEPT_FORM}>
					Add Creatives to Concept
				</Button>
			</>
		);
	};

	return (
		<Blade
			type="modal"
			setAppTitle={false}
			className="add-ad-studio-creative"
			title={`Add New Ad Studio Creative to - ${modalOptions?.creativeName}`}
			allowFullScreen={false}
			xclose
			bladeProps={{ verticallyCenter: true }}
			onClose={onClose}
			renderContent={renderContent}
			renderFooter={renderFooter}
		/>
	);
}

export default reduxForm({
	form: ADD_ADSTUDIO_TO_CONCEPT_FORM,
	initialValues: {
		"base-sizes": {},
		"custom-sizes": [],
	},
	validate: customValidate,
	onSubmit(values, dispatch, formProps){
		dispatch(addNewAdstudioCreativesToConcept(formProps.libraryId, { ...values }));
	},
	destroyOnUnmount: false,
})(AddAdStudioCreativeToConcept);
